import { PaymentMethod } from './api.settings';

const BASE = '/api';

export interface BookingSitePhoto {
  id: number;
  url: string;
  caption: string | null;
  sortOrder: number;
}

export interface BookingSite {
  id: number | null;
  apartmentId: number;
  slug: string;
  title: string | null;
  introduction: string | null;
  category: string | null;
  listingType: string | null;
  bedroomsCount: number | null;
  bathroomsCount: number | null;
  maxGuests: number | null;
  bedsJson: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  showExactAddress: boolean;
  arrivalInstructions: string | null;
  neighborhoodHighlights: string | null;
  amenitiesJson: string | null;
  basePrice: number | null;
  raisedPriceJson: string | null;
  cleaningFee: number | null;
  petFee: number | null;
  extraGuestFee: number | null;
  extraGuestAfter: number | null;
  additionalFeesJson: string | null;
  availabilityMonths: number;
  checkinTime: string | null;
  checkoutTime: string | null;
  petsAllowed: boolean;
  partiesAllowed: boolean;
  smokingAllowed: boolean;
  cancellationPolicy: 'FLEXIBLE' | 'MODERATE' | 'STRICT';
  paymentMethodIds: number[];
  paymentMethods: PaymentMethod[];
  logoUrl: string | null;
  colorTheme: string;
  faviconEmoji: string | null;
  isPublished: boolean;
  htmlGeneratedAt: string | null;
  photos: BookingSitePhoto[];
}

export const emptyBookingSite = (apartmentId: number): BookingSite => ({
  id: null,
  apartmentId,
  slug: '',
  title: null,
  introduction: null,
  category: null,
  listingType: null,
  bedroomsCount: null,
  bathroomsCount: null,
  maxGuests: null,
  bedsJson: null,
  streetAddress: null,
  city: null,
  state: null,
  postalCode: null,
  country: null,
  showExactAddress: true,
  arrivalInstructions: null,
  neighborhoodHighlights: null,
  amenitiesJson: null,
  basePrice: null,
  raisedPriceJson: null,
  cleaningFee: null,
  petFee: null,
  extraGuestFee: null,
  extraGuestAfter: null,
  additionalFeesJson: null,
  availabilityMonths: 6,
  checkinTime: null,
  checkoutTime: null,
  petsAllowed: false,
  partiesAllowed: false,
  smokingAllowed: false,
  cancellationPolicy: 'FLEXIBLE',
  paymentMethodIds: [],
  paymentMethods: [],
  logoUrl: null,
  colorTheme: 'emerald',
  faviconEmoji: null,
  isPublished: false,
  htmlGeneratedAt: null,
  photos: [],
});

export async function getBookingSite(apartmentId: number, token: string): Promise<BookingSite> {
  const res = await fetch(`${BASE}/apartments/${apartmentId}/booking-site`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function saveBookingSite(apartmentId: number, data: BookingSite, token: string): Promise<BookingSite> {
  const res = await fetch(`${BASE}/apartments/${apartmentId}/booking-site`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Save failed' }));
    throw new Error(err.message || 'Save failed');
  }
  return res.json();
}

export async function publishBookingSite(apartmentId: number, token: string): Promise<BookingSite> {
  const res = await fetch(`${BASE}/apartments/${apartmentId}/booking-site/publish`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Publish failed' }));
    throw new Error(err.message || 'Publish failed');
  }
  return res.json();
}

export async function uploadBookingSitePhoto(
  apartmentId: number, file: File, caption: string | null, token: string
): Promise<BookingSitePhoto> {
  const fd = new FormData();
  fd.append('file', file);
  if (caption) fd.append('caption', caption);
  const res = await fetch(`${BASE}/apartments/${apartmentId}/booking-site/photos`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(err.message || 'Upload failed');
  }
  return res.json();
}

export async function deleteBookingSitePhoto(apartmentId: number, photoId: number, token: string): Promise<void> {
  const res = await fetch(`${BASE}/apartments/${apartmentId}/booking-site/photos/${photoId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Delete failed');
}

export async function reorderBookingSitePhotos(apartmentId: number, orderedIds: number[], token: string): Promise<void> {
  const res = await fetch(`${BASE}/apartments/${apartmentId}/booking-site/photos/order`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(orderedIds),
  });
  if (!res.ok) throw new Error('Reorder failed');
}

export async function uploadBookingSiteLogo(apartmentId: number, file: File, token: string): Promise<BookingSite> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE}/apartments/${apartmentId}/booking-site/logo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Logo upload failed' }));
    throw new Error(err.message || 'Logo upload failed');
  }
  return res.json();
}

export async function deleteBookingSiteLogo(apartmentId: number, token: string): Promise<BookingSite> {
  const res = await fetch(`${BASE}/apartments/${apartmentId}/booking-site/logo`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Logo delete failed');
  return res.json();
}
